/**
 * InnBotics AI Agent Service
 * Wraps calls to the FastAPI AI backend (port 8001).
 */

const AI_BASE_URL = process.env.REACT_APP_AI_API_URL || 'http://localhost:8001';

/**
 * Create a new agent session.
 * @param {string} agentType - "auto" | "booking" | "concierge" | "manager"
 * @param {object} context   - { hotel_id, booking_id, room_number, guest_name, is_checked_in, role }
 * @param {string} token     - Bearer JWT (optional)
 * @returns {Promise<{session_id, agent_type, context}>}
 */
export async function createSession(agentType = 'auto', context = {}, token = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${AI_BASE_URL}/sessions`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ agent_type: agentType, context }),
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Failed to create AI session: ${err}`);
  }
  return res.json();
}

/**
 * Send a chat message to the orchestrated multi-agent endpoint.
 * @param {string} sessionId
 * @param {string} message
 * @param {string} token       - Bearer JWT (optional)
 * @param {string} forceAgent  - Force a specific agent (optional)
 * @returns {Promise<{session_id, agent_used, reply, actions, escalation}>}
 */
export async function sendChat(sessionId, message, token = null, forceAgent = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const body = { session_id: sessionId, message };
  if (forceAgent) body.force_agent = forceAgent;

  const res = await fetch(`${AI_BASE_URL}/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.text();
    const e = new Error(`AI chat error: ${err}`);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

/**
 * Send directly to the booking agent (no session needed).
 */
export async function bookingChat(message, history = [], context = {}, token = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${AI_BASE_URL}/booking/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ message, history, context }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Send directly to the concierge agent (no session needed).
 */
export async function conciergeChat(message, history = [], context = {}, token = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${AI_BASE_URL}/concierge/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ message, history, context }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Send directly to the manager agent (no session needed).
 */
export async function managerChat(message, history = [], context = {}, token = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${AI_BASE_URL}/manager/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ message, history, context }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Health check — returns true if AI server is reachable.
 */
export async function checkAIHealth() {
  try {
    const res = await fetch(`${AI_BASE_URL}/health`, { signal: AbortSignal.timeout(3000) });
    return res.ok;
  } catch {
    return false;
  }
}
